// src/pages/materials/components/MaterialDrawer.jsx
import React, { useMemo, useEffect, useRef, useCallback } from "react";
import {
  Drawer, Space, Button, Form, Input, Select,
  InputNumber, DatePicker, Popconfirm, Collapse
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import MaterialImages from "../MaterialImages.jsx";
import ColorSelect from "../ColorSelect.jsx";
import CompositionSelect from "../CompositionSelect.jsx";
import BarcodeScanner from "../BarcodeScanner.jsx";


// ⬇️ новый импорт утилит
import {
  toId,
  eanValidator,
  buildInitial,
} from "./utils/materialsFormHelpers";


export default function MaterialDrawer({
  open, onClose, onSubmit, onDelete,
  editing, parents, childrenByParent, units, suppliers
}) {
  const [form] = Form.useForm();

  const title = editing?._id ? `Редагування: ${editing.name || ""}` : "Новий матеріал";

  // стабильный id текущей карточки
  const editingId = editing?._id || "new";

  // рядом с импортами
const EMPTY_INITIAL = {
  code: "",
  name: "",
  section_id: undefined,
  category_id: undefined,
  images: [],
  ean: "",
  colors: [],
  patterns: [],
  composition_parts: [],
  characteristics: "",
  unit_id: undefined,
  unit_name: null,
  note: "",
  supplier_id: undefined,
  supplier_name: null,
  purchased_at: null,   // или undefined
  quantity: null,       // или 0, если нужно
  price: null,
  color: "",            // у вас формируется из colors[0], но пусть будет явный дефолт
};



  // Мемо initial без побочных эффектов
  const initial = useMemo(
    () => buildInitial(editing, parents, childrenByParent, suppliers, units),
    [editingId, parents, childrenByParent, suppliers, units]
  );

  // === Полная перезагрузка формы на каждое открытие новой карточки ===
  // 1) ключи на Drawer и Form -> гарантированный ремоунт узлов
  const drawerKey = `drawer-${editingId}-${open ? 1 : 0}`;
  const formKey = `form-${editingId}-${open ? 1 : 0}`;

  // 2) инициализация значений после маунта формы
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    // жёсткая синхронизация — без гонок с опциями
    form.setFieldsValue(initial);
    return () => { mountedRef.current = false; };
  }, [formKey]);

  // 3) при смене section сбрасываем категорию (только при реальном изменении)
  const sectionId = Form.useWatch("section_id", form);
  const prevSectionRef = useRef();
  useEffect(() => {
    if (!mountedRef.current) return;
    if (prevSectionRef.current === undefined) {
      prevSectionRef.current = sectionId;
      return;
    }
    if (prevSectionRef.current !== sectionId) {
      form.setFieldsValue({ category_id: undefined });
      prevSectionRef.current = sectionId;
    }
  }, [sectionId, form]);

  // 4) Вспомогательные вотчеры
  const eanWatch = Form.useWatch("ean", form);

  // options
  const parentOptions = useMemo(
    () => (parents || []).map(p => ({ value: toId(p._id || p.id), label: p.name })),
    [parents]
  );
  const unitOptions = useMemo(
    () => (units || []).map(u => ({ value: toId(u._id || u.id), label: u.name })),
    [units]
  );
  const supplierOptions = useMemo(
    () => (suppliers || []).map(s => ({ value: toId(s._id || s.id), label: s.name })),
    [suppliers]
  );

  const childOptions = useMemo(() => {
    const list = sectionId ? (childrenByParent?.[sectionId] || []) : [];
    return list.map(c => ({ value: toId(c._id || c.id), label: c.name }));
  }, [childrenByParent, sectionId]);

  // селекты с записью имён
  const handleSupplierChange = (value, option) => {
    form.setFieldsValue({
      supplier_id: value || undefined,
      supplier_name: option?.label || null,
    });
  };
  const handleUnitChange = (value, option) => {
    form.setFieldsValue({
      unit_id: value || undefined,
      unit_name: option?.label || null,
    });
  };

  const handleScan = useCallback((raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "");
    if (!digits) return;
    form.setFieldValue?.("ean", digits);
    form.setFieldsValue?.({ ean: digits });
    form.validateFields?.(["ean"]).catch(() => {});
  }, [form]);

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const children = childrenByParent?.[values.section_id] || [];
    const isChild = children.some(c => toId(c._id || c.id) === values.category_id);
    if (!isChild) {
      form.setFields([{ name: "category_id", errors: ["Обрана категорія не належить до вибраного розділу"] }]);
      return;
    }

    const section = (parents || []).find(p => toId(p._id || p.id) === values.section_id);
    const category = children.find(c => toId(c._id || c.id) === values.category_id);
    const supplier_name = values.supplier_name
      ?? ((suppliers || []).find(s => toId(s._id || s.id) === values.supplier_id)?.name || null);
    const unit_name = values.unit_name
      ?? ((units || []).find(u => toId(u._id || u.id) === values.unit_id)?.name || null);

    const colors = Array.isArray(values.colors) ? values.colors : [];
    const patterns = Array.isArray(values.patterns) ? values.patterns : [];
    const images = Array.isArray(values.images) ? values.images : [];

    const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
    const clamp01 = (n) => {
      const x = Number(n);
      if (!Number.isFinite(x)) return undefined;
      if (x < 0) return 0;
      if (x > 100) return 100;
      return round2(x);
    };
    const partsRaw = Array.isArray(values.composition_parts) ? values.composition_parts : [];
    const partsClean = partsRaw
      .filter(p => p && (p.name ?? p.value ?? p.label))
      .map(p => {
        const name = String((p.label ?? p.name ?? p.value) || "").trim();
        const percent = clamp01(p.percent);
        return { name, percent };
      })
      .filter(p => p.name && p.percent != null);

    const total = round2(partsClean.reduce((a, it) => a + (Number(it.percent) || 0), 0));
    if (partsClean.length > 0) {
      const diff = round2(100 - total);
      if (Math.abs(diff) <= 0.5) {
        const last = partsClean.length - 1;
        partsClean[last] = { ...partsClean[last], percent: clamp01(round2((partsClean[last].percent || 0) + diff)) };
      }
    }

    const totalAfter = round2(partsClean.reduce((a, it) => a + (Number(it.percent) || 0), 0));
    if (totalAfter > 100.0 + 0.01) {
      form.setFields([{ name: "composition_parts", errors: ["Сума відсотків перевищує 100%"] }]);
      return;
    }

    const composition = partsClean.length
      ? partsClean.map(p => `${p.name} ${Math.round(p.percent)}%`).join(", ")
      : (values.composition || "");

    const payload = {
      ...values,
      // _id НЕ отправляем в теле
      images,
      group: section?.name || null,
      section_name: section?.name || null,
      category_name: category?.name || null,
      supplier_name,
      unit_name,
      colors,
      patterns,
      color: colors[0] || values.color || "",
      composition_parts: partsClean,
      composition,
    };

    if (payload.purchased_at) {
      payload.purchased_at = payload.purchased_at.toISOString();
    }

    // Перестраховка от случайного попадания _id
    const { _id: _ignored, ...safePayload } = payload;
    await onSubmit(safePayload);
  };

  return (
    <Drawer

      title={title}
      open={open}
      onClose={onClose}
      //destroyOnHidden
      placement="right"
      width={520}

      forceRender={true}        // один раз примонтировали — дальше просто показываем
      footer={
        <Space style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          {editing?._id ? (
            <Popconfirm
              title="Видалити матеріал?"
              okText="Видалити"
              cancelText="Скасувати"
              okButtonProps={{ danger: true }}
              onConfirm={() => onDelete(editing._id)}
            >
              <Button danger icon={<DeleteOutlined />}>Видалити</Button>
            </Popconfirm>
          ) : <span />}
          <Space>
            <Button onClick={onClose}>Скасувати</Button>
            <Button type="primary" onClick={handleSubmit}>Зберегти</Button>
          </Space>
        </Space>
      }
      bodyStyle={{ paddingBottom: 8 }}
    >
      <Form
        key={formKey}
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initial}
      >
        {/* ===== НЕ сворачивается: код, наименование, раздел/категория, фото ===== */}
        <Form.Item name="code" label="Код матеріалу" rules={[{ required: true, message: "Вкажіть код" }]} preserve={false}>
          <Input placeholder="Напр.: MAT-00123" />
        </Form.Item>

        <Form.Item name="name" label="Найменування матеріалу" rules={[{ required: true, min: 2, message: "Вкажіть назву" }]} preserve={false}>
          <Input placeholder="Напр.: Бавовна полотняна" />
        </Form.Item>

        <Form.Item name="section_id" label="Розділ" rules={[{ required: true, message: "Оберіть розділ" }]} preserve={false}>
          <Select
            placeholder="Оберіть розділ"
            options={parentOptions}
            optionFilterProp="label"
            showSearch
            onChange={() => {
              // сбрасываем категорию только при ЯВНОМ выборе пользователем
              form.setFieldsValue({ category_id: undefined });
            }}
          />
        </Form.Item>

        <Form.Item name="category_id" label="Категорія" rules={[{ required: true, message: "Оберіть категорію" }]} preserve={false}>
          <Select
            placeholder="Оберіть категорію"
            options={childOptions}
            disabled={!sectionId}
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>

        <Form.Item name="images" label="Фото" valuePropName="value" preserve={false}>
          <MaterialImages key={`images-${formKey}`} />
        </Form.Item>

        {/* ===== СВОРАЧИВАЕМЫЕ БЛОКИ ===== */}
        <Collapse ghost style={{ marginTop: 8 }}
          items={[
            {
              key: 'ean',
              label: 'Штрих‑код (EAN) / Сканер',
              children: (
                <div>
                  <Form.Item name="ean" label="EAN / Штрих-код" tooltip="Можна ввести вручну або відсканувати" rules={[{ validator: eanValidator }]} preserve={false}>
                    <Input
                      placeholder="Напр.: 4820001234567"
                      inputMode="numeric"
                      pattern="\\d*"
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        if (digits !== e.target.value) {
                          form.setFieldValue?.("ean", digits);
                        }
                      }}
                    />
                  </Form.Item>

                  <div style={{ marginTop: -4, marginBottom: 16 }}>
                    <BarcodeScanner onScan={handleScan} />
                    <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                      Останній скан: <code>{eanWatch || "—"}</code>
                    </div>
                  </div>
                </div>
              )
            },
            {
              key: 'characteristics',
              label: 'Характеристики',
              children: (
                <div>
                  <Form.Item name="colors" label="Кольори" preserve={false}>
                    <ColorSelect key={`colors-${formKey}`} kind="colors" multiple placeholder="Оберіть кольори" />
                  </Form.Item>
                  <Form.Item name="patterns" label="Узори" preserve={false}>
                    <ColorSelect key={`patterns-${formKey}`} kind="patterns" multiple placeholder="Оберіть узори" />
                  </Form.Item>
                  <Form.Item name="composition_parts" label="Склад (матеріал + %)" preserve={false}>
                    <CompositionSelect key={`composition-${formKey}`} />
                  </Form.Item>
                  <Form.Item name="characteristics" label="Характеристики (Щільність / Розмір)" preserve={false}>
                    <Input placeholder="Напр.: 160 г/м²; 150×200 см" />
                  </Form.Item>
                  <Form.Item name="unit_id" label="Одиниця вимірювання" preserve={false}>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Оберіть одиницю"
                      options={unitOptions}
                      optionFilterProp="label"
                      onChange={handleUnitChange}
                    />
                  </Form.Item>

                  <Form.Item name="note" label="Примітка" preserve={false}>
                    <Input.TextArea rows={3} placeholder="Додаткова інформація" />
                  </Form.Item>

                  {/* скрытые имя-поля для бэка */}
                  <Form.Item name="supplier_name" hidden><Input /></Form.Item>
                  <Form.Item name="unit_name" hidden><Input /></Form.Item>
                </div>
              )
            },
            {
              key: 'stock',
              label: 'Кількість / Ціна',
              children: (
                <div>
                  <Form.Item name="quantity" label="Кількість" preserve={false}>
                    <InputNumber style={{ width: "100%" }} min={0} step={1} placeholder="Напр.: 10" />
                  </Form.Item>
                  <Form.Item name="price" label="Ціна закупівлі, грн" preserve={false}>
                    <InputNumber style={{ width: "100%" }} min={0} placeholder="Напр.: 250.00" />
                  </Form.Item>
                </div>
              )
            },
            {
              key: 'supplier',
              label: 'Постачальник',
              children: (
                <div>
                  <Form.Item name="supplier_id" label="Виробник / Постачальник" preserve={false}>
                    <Select
                      showSearch
                      allowClear
                      placeholder="Оберіть постачальника"
                      options={supplierOptions}
                      optionFilterProp="label"
                      onChange={handleSupplierChange}
                    />
                  </Form.Item>
                  <Form.Item name="purchased_at" label="Дата закупівлі / оновлення" preserve={false}>
                    <DatePicker style={{ width: "100%" }} placeholder="Оберіть дату" />
                  </Form.Item>
                </div>
              )
            }
          ]}
        />
      </Form>
    </Drawer>
  );
}
